import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  InputAdornment,
  alpha,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { DevicesOutlined } from "@mui/icons-material";
import { UserService } from "../../services/UserService";
import { UtilityService } from "../../services/UtilityService";
import { BillingService } from "../../services/BillingService";
import { logger } from "../../utils/logger";

function ChangeDeviceLimitDialog({
  open,
  onClose,
  currentLimit,
  currentUsage,
  user,
}) {
  const { t } = useTranslation("common");
  const theme = useTheme();
  const [newLimit, setNewLimit] = useState(currentLimit || 0);
  const [loading, setLoading] = useState(false);

  const handleLimitChange = (event) => {
    setNewLimit(parseInt(event.target.value) || 0);
  };

  const handleUpgrade = async () => {
    if (newLimit < currentUsage) {
      UtilityService.updateSnackbarMessage(
        t("common.limit_increase_lower_error")
      );
      return;
    }

    if (newLimit === currentLimit) {
      UtilityService.updateSnackbarMessage(
        t("common.no_changes_made_device_limit")
      );
      onClose();
      return;
    }

    setLoading(true);

    try {
      if (!user.stripe_customer_id && !user.stripe_payment_method) {
        UtilityService.updateSnackbarMessage(t("common.card_capture_session"));
        const redirectURL = await BillingService.getCardCaptureSession();
        window.location.href = redirectURL;
      } else {
        const resp = await UserService.upgradeSubscription(newLimit);
        if (resp.session_url) {
          window.location.href = resp.session_url;
        } else {
          if (resp.status && resp.status === 200) {
            UtilityService.updateSnackbarMessage(
              t("common.limit_increase_success")
            );
            onClose();
            // Optionally refresh the page or update the state
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          } else {
            UtilityService.updateSnackbarMessage(
              t("common.limit_increase_error")
            );
          }
        }
      }
    } catch (error) {
      logger.error("Error upgrading subscription:", error);
      UtilityService.updateSnackbarMessage(t("common.limit_increase_error"));
    } finally {
      setLoading(false);
    }
  };

  const costCalculation = Math.max(0, newLimit - currentLimit) * 15;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          background:
            theme.palette.mode === "dark"
              ? "linear-gradient(145deg, rgba(40,40,45,0.95) 0%, rgba(35,35,40,0.9) 100%)"
              : "linear-gradient(145deg, rgba(255,255,255,0.95) 0%, rgba(245,245,245,0.9) 100%)",
          backdropFilter: "blur(20px)",
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h5" fontWeight={600}>
          Change Device Limit
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Adjust your organization's device limit
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        {/* Current Status */}
        <Box
          sx={{
            p: 2,
            mb: 3,
            borderRadius: "12px",
            backgroundColor: alpha(theme.palette.background.default, 0.5),
            border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          }}
        >
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Current Status
          </Typography>
          <Typography variant="body1">
            <strong>{currentUsage}</strong> devices used of{" "}
            <strong>{currentLimit}</strong> device limit
          </Typography>
        </Box>

        {/* New Limit Input */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={500} gutterBottom>
            New Device Limit
          </Typography>
          <TextField
            fullWidth
            type="number"
            variant="outlined"
            value={newLimit}
            onChange={handleLimitChange}
            inputProps={{ min: currentUsage || 0 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <DevicesOutlined color="action" />
                </InputAdornment>
              ),
            }}
            helperText={`Minimum: ${currentUsage} devices (current usage)`}
          />
        </Box>

        {/* Cost Display */}
        {newLimit > currentLimit && (
          <Box
            sx={{
              p: 3,
              borderRadius: "12px",
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            }}
          >
            <Typography variant="body1" gutterBottom>
              {user.stripe_subscription_id
                ? "Upgrade Cost"
                : "Total Cost Due Today"}
            </Typography>
            <Typography variant="h4" fontWeight={700} color="primary">
              £{costCalculation}
              <Typography
                variant="body1"
                component="span"
                color="text.secondary"
                sx={{ ml: 1 }}
              >
                per year
              </Typography>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Additional {newLimit - currentLimit} devices × £20/year each
            </Typography>
          </Box>
        )}

        {newLimit < currentLimit && (
          <Box
            sx={{
              p: 3,
              borderRadius: "12px",
              backgroundColor: alpha(theme.palette.warning.main, 0.05),
              border: `1px solid ${alpha(theme.palette.warning.main, 0.1)}`,
            }}
          >
            <Typography variant="body2" color="warning.main">
              <strong>Note:</strong> Reducing your device limit below current
              usage may require removing devices first.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ borderRadius: "8px", px: 3 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleUpgrade}
          variant="contained"
          disabled={loading || newLimit < currentUsage}
          sx={{
            borderRadius: "8px",
            px: 3,
            backgroundColor:
              theme.palette.mode === "dark"
                ? "#00c9a7"
                : theme.palette.primary.main,
            color: theme.palette.mode === "dark" ? "#000" : "#fff",
            "&:hover": {
              backgroundColor:
                theme.palette.mode === "dark"
                  ? alpha("#00c9a7", 0.9)
                  : alpha(theme.palette.primary.main, 0.9),
            },
          }}
        >
          {loading
            ? "Processing..."
            : newLimit > currentLimit
              ? "Upgrade Limit"
              : "Update Limit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ChangeDeviceLimitDialog;
